import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState, useMemo } from 'react';
import {
  useScheme,
  useSchemeCycles,
  useSchemeCommissions,
  useApproveCommission,
} from '../../../../features/schemes/network';
import type { CommissionDto } from '../../../../features/schemes/network/commission.api';
import type { SchemeDetail } from '../../../../features/schemes/network';

type SchemeWithFields = SchemeDetail & {
  displayName?: string;
  gstApplicable?: boolean;
  chitValue?: number;
  commissionRate?: number;
};

// tableData row shape: the raw CommissionDto plus fields joined/derived client-side (cycle
// number/date from cycleMap, GST/TDS figures computed from scheme config) -- distinct from
// CommissionDto itself, which only has what the commission API actually returns.
type CommissionRow = CommissionDto & {
  cycleNum: number;
  scheduledDate: string;
  gstAmount: number;
  gstRate: number;
  tdsRate: number;
};
import { StatCard } from '../../../../components/layout/StatCard';
import { StatusBadge } from '../../../../components/ui/StatusBadge';
import { Button } from '../../../../components/ui/Button';
import { ConfirmDialog } from '../../../../components/ui/ConfirmDialog';
import { DEFAULT_CURRENCY, DEFAULT_LOCALE } from '../../../../lib/locale';
import { formatMoney } from '@repo/utils';
import { useToast } from '../../../../components/ui/Toast';
import { usePageTitle } from '../../../../lib/hooks';
import { PageHeader } from '../../../../components/layout/PageHeader';
import { Skeleton } from '../../../../components/ui/Skeleton';

export const Route = createFileRoute('/_auth/schemes/$id/commission')({
  component: SchemeCommissionPage,
});

function SchemeCommissionPage() {
  usePageTitle('Foreman Commission');
  const { id: schemeId } = Route.useParams();
  const { toast } = useToast();

  const [page, setPage] = useState(1);
  const [selectedCommission, setSelectedCommission] = useState<CommissionRow | null>(null);
  const [showConfirmId, setShowConfirmId] = useState<string | null>(null);
  const [showDrawer, setShowDrawer] = useState(false);

  const { data: schemeRaw, isLoading: schemeLoading, error: schemeError } = useScheme(schemeId);
  const scheme = schemeRaw as SchemeWithFields | undefined;
  const { data: cycles, isLoading: cyclesLoading } = useSchemeCycles(schemeId);
  const { data: summary, isLoading: summaryLoading, error: summaryError } = useSchemeCommissions(schemeId);

  const approveMutation = useApproveCommission(schemeId);

  const cycleMap = useMemo(() => {
    const map = new Map<string, { cycleNumber: number; scheduledDate: string }>();
    if (cycles) {
      for (const c of cycles) {
        map.set(c.id, { cycleNumber: c.cycleNumber, scheduledDate: c.scheduledDate });
      }
    }
    return map;
  }, [cycles]);

  const tableData = useMemo(() => {
    if (!summary?.records) return [];
    return summary.records.map((r: CommissionDto) => {
      const cycleInfo = cycleMap.get(r.cycleId);
      const cycleNum = cycleInfo?.cycleNumber ?? 0;
      const scheduledDate = cycleInfo?.scheduledDate ?? '—';
      const gstRate = scheme?.gstApplicable ? 0.18 : 0;
      const gstAmount = Math.floor(r.grossCommission * gstRate);

      return {
        ...r,
        cycleNum,
        scheduledDate,
        gstAmount,
        gstRate,
        tdsRate: r.grossCommission > 0 ? r.tdsAmount / r.grossCommission : 0.10,
      };
    });
  }, [summary, cycleMap, scheme]);

  const kpis = useMemo(() => {
    let totalTds = 0;
    let totalGst = 0;
    let pendingCount = 0;

    if (tableData) {
      for (const item of tableData) {
        totalTds += item.tdsAmount;
        totalGst += item.gstAmount;
        if (item.status === 'COMPUTED') {
          pendingCount += 1;
        }
      }
    }

    return {
      totalEarned: summary?.totalEarned ?? 0,
      pendingCount,
      totalGst,
      totalTds,
    };
  }, [tableData, summary]);

  const handleApprove = async () => {
    if (!showConfirmId) return;
    try {
      await approveMutation.mutateAsync(showConfirmId);
      toast({
        type: 'success',
        title: 'Commission Approved',
        description: 'Ledger updated and payout instruction generated successfully.',
      });
      setShowConfirmId(null);
    } catch (err) {
      console.error('Failed to approve commission', err);
      toast({
        type: 'error',
        title: 'Approval Failed',
        description: (err as { message?: string }).message || 'Commission approval failed. Verify the payout details are correct and try again.',
      });
    }
  };

  const getStatusType = (status: string): 'success' | 'warning' | 'danger' | 'info' => {
    switch (status) {
      case 'PAID':
        return 'success';
      case 'APPROVED':
      case 'PAYOUT_INSTRUCTED':
        return 'info';
      case 'COMPUTED':
        return 'warning';
      default:
        return 'info';
    }
  };

  const isLoading = schemeLoading || summaryLoading || cyclesLoading;
  const isError = schemeError || summaryError || !scheme;

  return (
    <div className="flex flex-col w-full h-full" style={{ viewTransitionName: 'page-body' }}>
      <PageHeader
        title="Foreman Commission Register"
        subtitle={`Manage, audit, and approve foreman commissions for ${scheme?.displayName || schemeId}`}
        backTo={{ to: "/schemes/$id", params: { id: schemeId } }}
      />
      <div className="flex items-center gap-2 text-sm text-text-muted mb-4">
        <Link to="/schemes" className="hover:text-text-primary">Schemes</Link>
        <span>/</span>
        <Link to="/schemes/$id" params={{ id: schemeId }} className="hover:text-text-primary">
          {scheme?.displayName || schemeId.slice(0, 8)}
        </Link>
        <span>/</span>
        <span className="text-text-primary">Foreman Commissions</span>
      </div>

      {isLoading ? (
        <Skeleton rows={8} />
      ) : isError ? (
        <div className="text-danger-text p-8 text-center">Failed to load scheme commission register.</div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <StatCard
              label="Total Commission Earned"
              value={formatMoney(kpis.totalEarned, { currency: DEFAULT_CURRENCY, locale: DEFAULT_LOCALE })}
            />
            <StatCard
              label="Pending Approvals"
              value={`${kpis.pendingCount} Cycles`}
            />
            <StatCard
              label="GST Contributed"
              value={formatMoney(kpis.totalGst, { currency: DEFAULT_CURRENCY, locale: DEFAULT_LOCALE })}
            />
            <StatCard
              label="TDS Deducted"
              value={formatMoney(kpis.totalTds, { currency: DEFAULT_CURRENCY, locale: DEFAULT_LOCALE })}
            />
          </div>

          <div className="bg-surface-1 rounded-[10px] border border-border p-6">
            <h2 className="text-lg font-semibold text-text-primary mb-4">Commission Cycle Entries</h2>
            <div className="bg-surface-1 border border-border rounded-xl overflow-hidden shadow-sm">
              <table className="w-full border-collapse text-left text-sm">
                <thead className="bg-surface-2 text-text-secondary font-semibold uppercase tracking-wider text-xs border-b border-border">
                  <tr>
                    <th className="p-4">Cycle #</th>
                    <th className="p-4">Gross Commission</th>
                    <th className="p-4">GST ({scheme?.gstApplicable ? '18%' : '0%'})</th>
                    <th className="p-4">TDS (10%)</th>
                    <th className="p-4">Net Commission</th>
                    <th className="p-4">Status</th>
                    <th className="p-4">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border text-text-primary">
                  {tableData.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="p-8 text-center text-text-muted">No commission records</td>
                    </tr>
                  ) : (
                    tableData.map(row => (
                      <tr key={row.id} className="hover:bg-surface-2 transition-colors">
                        <td className="p-4">
                          <Button
                            variant="ghost"
                            onClick={() => {
                              setSelectedCommission(row);
                              setShowDrawer(true);
                            }}
                            className="text-accent-600 hover:underline font-semibold p-0 h-auto"
                          >
                            Cycle {row.cycleNum}
                          </Button>
                        </td>
                        <td className="p-4 font-mono">{formatMoney(row.grossCommission, { currency: DEFAULT_CURRENCY, locale: DEFAULT_LOCALE })}</td>
                        <td className="p-4 font-mono">{formatMoney(row.gstAmount, { currency: DEFAULT_CURRENCY, locale: DEFAULT_LOCALE })}</td>
                        <td className="p-4 font-mono">{formatMoney(row.tdsAmount, { currency: DEFAULT_CURRENCY, locale: DEFAULT_LOCALE })}</td>
                        <td className="p-4 font-mono">{formatMoney(row.netCommission, { currency: DEFAULT_CURRENCY, locale: DEFAULT_LOCALE })}</td>
                        <td className="p-4"><StatusBadge status={getStatusType(row.status)} label={row.status} /></td>
                        <td className="p-4">
                          {row.status === 'COMPUTED' ? (
                            <Button
                              size="sm"
                              variant="primary"
                              className="bg-success-500 hover:bg-success-600 text-white"
                              onClick={() => setShowConfirmId(row.id)}
                            >
                              Approve Commission
                            </Button>
                          ) : (
                            <span className="text-xs text-text-muted">Approved</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between mt-4 text-sm text-text-muted">
              <span>Page {page}</span>
              <div className="flex gap-2">
                <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
                <Button variant="secondary" size="sm" disabled={tableData.length <= page * 10} onClick={() => setPage(p => p + 1)}>Next</Button>
              </div>
            </div>
          </div>

          {showDrawer && selectedCommission && (
            <>
              <div className="fixed inset-0 bg-black/40 z-overlay transition-opacity" onClick={() => setShowDrawer(false)} />
              <div className="fixed inset-y-0 right-0 z-modal w-full max-w-md bg-surface-1 border-l border-border shadow-2xl p-6 overflow-y-auto flex flex-col justify-between">
                <div className="space-y-6">
                  <div className="flex items-center justify-between border-b border-border pb-4">
                    <h3 className="text-lg font-bold text-text-primary">
                      Commission Receipt — Cycle {selectedCommission.cycleNum}
                    </h3>
                    <Button variant="ghost" onClick={() => setShowDrawer(false)} className="text-text-muted hover:text-text-primary text-xl font-bold p-1">&times;</Button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <span className="text-xs text-text-muted uppercase tracking-wider font-semibold block">Winning Discount</span>
                      <span className="text-lg font-medium text-text-primary">{formatMoney(selectedCommission.winningDiscount, { currency: DEFAULT_CURRENCY, locale: DEFAULT_LOCALE })}</span>
                    </div>
                    <div className="border-t border-border pt-4 space-y-3">
                      <div className="flex justify-between text-sm">
                        <span className="text-text-secondary">Chit Value</span>
                        <span className="font-medium text-text-primary">{formatMoney(scheme.chitValue, { currency: DEFAULT_CURRENCY, locale: DEFAULT_LOCALE })}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-text-secondary">Foreman Commission Rate</span>
                        <span className="font-medium text-text-primary">{((scheme.commissionRate ?? 0) * 100).toFixed(2)}%</span>
                      </div>
                      <div className="flex justify-between text-sm font-semibold border-b border-border pb-2">
                        <span className="text-text-primary">(=) Gross Foreman Commission</span>
                        <span className="text-text-primary">{formatMoney(selectedCommission.grossCommission, { currency: DEFAULT_CURRENCY, locale: DEFAULT_LOCALE })}</span>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="flex justify-between text-sm">
                        <span className="text-text-secondary">(-) TDS Deducted ({(selectedCommission.tdsRate * 100).toFixed(0)}%)</span>
                        <span className="font-medium text-danger-text">{formatMoney(selectedCommission.tdsAmount, { currency: DEFAULT_CURRENCY, locale: DEFAULT_LOCALE })}</span>
                      </div>
                      <div className="flex justify-between text-base font-bold border-t border-border pt-2">
                        <span className="text-text-primary">(=) Net Disbursable Commission</span>
                        <span className="text-accent-600">{formatMoney(selectedCommission.netCommission, { currency: DEFAULT_CURRENCY, locale: DEFAULT_LOCALE })}</span>
                      </div>
                    </div>
                    {scheme.gstApplicable && (
                      <div className="bg-surface-2 p-4 rounded-[6px] border border-border space-y-2 mt-4">
                        <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide">GST Invoice Reference</p>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div><span className="text-text-muted block">Invoice Type</span><span className="font-medium text-text-primary">Foreman Commission Invoice</span></div>
                          <div><span className="text-text-muted block">GST Rate</span><span className="font-medium text-text-primary">18.00%</span></div>
                          <div><span className="text-text-muted block">CGST (9%)</span><span className="font-medium text-text-primary">{formatMoney(Math.floor(selectedCommission.gstAmount / 2), { currency: DEFAULT_CURRENCY, locale: DEFAULT_LOCALE })}</span></div>
                          <div><span className="text-text-muted block">SGST (9%)</span><span className="font-medium text-text-primary">{formatMoney(Math.floor(selectedCommission.gstAmount / 2), { currency: DEFAULT_CURRENCY, locale: DEFAULT_LOCALE })}</span></div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="border-t border-border pt-4 flex gap-3">
                  <Button variant="secondary" className="w-full" onClick={() => setShowDrawer(false)}>Close Details</Button>
                  {selectedCommission.status === 'COMPUTED' && (
                    <Button variant="primary" className="w-full bg-success-500 hover:bg-success-600 text-white" onClick={() => { setShowConfirmId(selectedCommission.id); setShowDrawer(false); }}>
                      Approve Commission
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}

          <ConfirmDialog
            open={showConfirmId !== null}
            title="Confirm Commission Approval"
            description="Approving this commission will issue a double-entry ledger entry transferring funds to the Foreman Commission account and generate a payout instruction. This action is irreversible."
            onConfirm={handleApprove}
            onCancel={() => setShowConfirmId(null)}
            loading={approveMutation.isPending}
            confirmLabel="Approve"
          />
        </>
      )}
    </div>
  );
}
